import { Button, Group, Box, useMantineTheme, Text } from "@mantine/core";
import { useForm, yupResolver } from "@mantine/form";
import ImageUpload from "src/components/ImageUpload";
import {
  CreateLocationInput,
  useCreateLocationMutation,
} from "src/generated/graphql";
import { memo } from "react";
import { useNavigate } from "react-router-dom";
import TextInput from "src/components/TextInput/TextInput";
import Textarea from "src/components/Textarea/Textarea";
import { object, string } from "yup";
import { ROUTES, TIMEZONES } from "src/utils/constants";
import Select from "src/components/Select";

type CreateLocationFormProps = {
  handleClose: () => void;
};

export const locationFormSchema = object().shape({
  name: string()
    .trim("No spaces allowed")
    .strict(true)
    .min(2, "Name must be at least 2 letters")
    .required("Name is required")
    .max(30, "Name must not be greater than 30 letters"),
  description: string().max(200).notRequired(),
  address: string().nullable().max(80),
  city: string().nullable().max(30),
  zip: string().nullable().max(12),
  state: string().nullable().max(50),
  // Contact Info
  firstName: string()
    .trim("No spaces allowed")
    .strict(true)
    .matches(/.{2,}/, {
      excludeEmptyString: true,
      message: "First Name must be at least 2 characters",
    })
    .max(50, "First Name must not be greater than 50 letters"),
  lastName: string()
    .trim("No spaces allowed")
    .strict(true)
    .matches(/.{2,}/, {
      excludeEmptyString: true,
      message: "Last Name must be at least 2 characters",
    })
    .max(50, "Last Name must not be greater than 50 letters"),
  email: string().email().nullable().max(50),
  notificationSenderNumber: string()
    .matches(/^(?:\+\d{9,11}|\d{10,11})?$/, {
      excludeEmptyString: true,
      message: "Enter a valid phone number",
    })
    .notRequired(),
});

export const locInitialValues = {
  name: "",
  description: "",
  address: "",
  city: "",
  zip: "",
  logoImage: "",
  state: "",
  stationsEnabled: false,
  notificationSenderNumber: "",
  timezone: "",
};

const CreateLocationForm = ({ handleClose }: CreateLocationFormProps) => {
  const theme = useMantineTheme();
  const navigate = useNavigate();

  const [createLoc, { loading, error, data }] = useCreateLocationMutation({
    onCompleted: () => {
      handleClose();
      navigate(`${ROUTES.LOCATION}/${data?.createLocation.id}`);
    },
  });

  const form = useForm({
    initialValues: locInitialValues,
    validate: yupResolver(locationFormSchema),
  });

  const createLocation = async (values: CreateLocationInput) => {
    await createLoc({
      variables: {
        data: values,
      },
    });
  };

  return (
    <Box sx={{ maxWidth: 360 }} mx="auto">
      <form
        onSubmit={form.onSubmit((values) => {
          createLocation(values);
        })}
      >
        <Text sx={{ fontWeight: 500 }} mb={"md"} align="center">
          Fill out the details for your new Location below
        </Text>
        <div
          style={{
            justifyContent: "center",
            alignItems: "center",
            display: "flex",
            maxWidth: 220,
            margin: "auto",
          }}
        >
          <ImageUpload
            onReadSuccess={(b64) => form.setFieldValue("logoImage", b64)}
            src={form.values.logoImage}
            height="170px"
            maxWidth="100%"
            alt="organization Logo"
          />
        </div>

        <TextInput withAsterisk label="Name" {...form.getInputProps("name")} />
        <Textarea label="Description" {...form.getInputProps("description")} />
        <TextInput label="Address" {...form.getInputProps("address")} />
        <TextInput label="City" {...form.getInputProps("city")} />
        <TextInput label="State" {...form.getInputProps("state")} />
        <TextInput label="Zip" {...form.getInputProps("zip")} />
        <TextInput
          label="Sender Phone Number"
          maxLength={11}
          {...form.getInputProps("notificationSenderNumber")}
        />
        <Select
          label="Timezone"
          data={
            TIMEZONES.map((item) => ({
              value: item.value,
              label: item.name,
            })) || []
          }
          {...form.getInputProps("timezone")}
        />

        <Group position="apart" mt="md" style={{ marginTop: 30 }}>
          <Button
            radius="md"
            variant="outline"
            onClick={handleClose}
            style={{
              flex: 1,
              color: theme.colors.secondaryTextColor[6],
            }}
            sx={{
              backgroundColor: theme.colors.secondary[6],
              "&:hover": {
                background: theme.colors.secondary[5],
              },
            }}
          >
            Cancel
          </Button>
          <Button
            radius="md"
            type="submit"
            disabled={loading}
            style={{
              flex: 1,
            }}
            sx={{
              color: theme.colors.primaryTextColor[6],
              "&:hover": {
                backgroundColor: theme.colors.primary[4],
              },
            }}
            loading={loading}
          >
            Create
          </Button>
        </Group>
      </form>
    </Box>
  );
};

export default memo(CreateLocationForm);
